import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { MaskLines } from "../site/Motion";
import { useSettings } from "../../lib/settings";

export const Hero = () => {
  const s = useSettings();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const rx = useSpring(useTransform(my, [0, 1], [7, -7]), { stiffness: 80, damping: 18 });
  const ry = useSpring(useTransform(mx, [0, 1], [-9, 9]), { stiffness: 80, damping: 18 });
  const spotX = useTransform(mx, (v) => `${v * 100}%`);
  const spotY = useTransform(my, (v) => `${v * 100}%`);
  const spotlight = useTransform([spotX, spotY], ([x, y]) => `radial-gradient(600px circle at ${x} ${y}, rgba(212,175,55,0.13), transparent 60%)`);
  const raw = (s.hero_title || "").split("\n").filter(Boolean);
  const lines = raw.map((l, i) => (i === raw.length - 1 && raw.length > 1 ? <em key={i} className="text-gold">{l}</em> : l));

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
  };

  return (
    <section ref={ref} onMouseMove={onMove} className="relative min-h-[100svh] overflow-hidden grain" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -top-[10%] h-[120%]">
        <motion.img
          src="/img/hero.jpg" alt="Commercial truck on the highway"
          initial={{ scale: 1.18, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 2.2, ease: [0.22, 1, 0.36, 1] }}
          className="w-full h-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-[linear-gradient(100deg,#0a0d14_8%,rgba(10,13,20,0.82)_45%,rgba(10,13,20,0.35)_100%)]" />
      <motion.div
        className="absolute inset-0 pointer-events-none hidden md:block"
        style={{ background: spotlight }}
      />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#0a0d14] to-transparent" />

      <motion.div style={{ y: textY }} className="relative max-w-7xl mx-auto px-5 md:px-10 pt-36 md:pt-44 pb-24 grid lg:grid-cols-12 gap-12 items-end min-h-[100svh]">
        <div className="lg:col-span-8">
          <motion.p initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1, duration: 0.8 }} className="eyebrow flex items-center gap-4" data-testid="hero-eyebrow">
            <span className="w-10 h-px bg-[var(--gold)]" /> {s.hero_eyebrow}
          </motion.p>
          <h1 className="font-display font-medium text-[3.4rem] leading-[0.95] sm:text-7xl lg:text-[6.1rem] text-slate-50 mt-8 tracking-[-0.01em]" data-testid="hero-title">
            <MaskLines lines={lines} delay={0.25} />
          </h1>
          <motion.p initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.95, duration: 0.9 }} className="mt-8 max-w-xl text-base md:text-lg text-slate-200 leading-relaxed" data-testid="hero-subtitle">
            {s.hero_subtitle}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.15, duration: 0.9 }} className="mt-10 flex flex-wrap gap-4">
            <Link to="/inventory" className="btn-gold" data-testid="hero-view-inventory-button">View All Inventory <ArrowRight className="w-4 h-4" /></Link>
            <Link to="/sourcing" className="btn-ghost" data-testid="hero-sourcing-button">Request an Asset</Link>
          </motion.div>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4, duration: 0.9 }} className="mt-10 flex items-center gap-3 font-mono text-[0.68rem] tracking-[0.25em] uppercase text-slate-300" data-testid="hero-established">
            <span className="w-2 h-2 rotate-45 border border-[var(--gold)]" /> Established 2024
          </motion.p>
        </div>

        <div className="lg:col-span-4 hidden lg:block [perspective:1200px]">
          <motion.div
            style={{ rotateX: rx, rotateY: ry }}
            initial={{ opacity: 0, y: 60 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="relative [transform-style:preserve-3d]"
            data-testid="hero-3d-card"
          >
            <div className="clip-frame relative aspect-[4/5] overflow-hidden border border-[rgba(212,175,55,0.4)]">
              <img src="/img/marine.jpg" alt="Marine vessel on open water" className="w-full h-full object-cover scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0a0d14] via-transparent to-transparent" />
            </div>
            <div className="absolute -left-10 bottom-10 bg-[#0a0d14]/90 backdrop-blur-xl border border-white/10 p-5 [transform:translateZ(60px)] w-60">
              <p className="eyebrow !text-[0.6rem]">Four divisions</p>
              <p className="font-display text-2xl text-slate-50 mt-2 leading-tight">Truck · Trailer · Marine · Equipment</p>
            </div>
          </motion.div>
        </div>
      </motion.div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.8 }} className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden md:flex flex-col items-center gap-2 text-slate-300">
        <span className="font-mono text-[0.6rem] tracking-[0.3em] uppercase">Scroll</span>
        <ArrowDown className="w-4 h-4 animate-bounce" />
      </motion.div>
    </section>
  );
};
